import { createContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'

import {
  getStoredAdminViewState,
  setStoredAdminViewState,
} from '../utils/adminViewStorage'

interface AdminViewContextType {
  /** Whether admin view is currently enabled (true) or user view is active (false) */
  isAdminViewEnabled: boolean
  /** Toggle between admin and user view */
  toggleAdminView: () => void
  /** Set admin view state explicitly */
  setAdminView: (enabled: boolean) => void
}

const AdminViewContext = createContext<AdminViewContextType | undefined>(
  undefined
)

interface AdminViewProviderProps {
  children: ReactNode
}

/**
 * Provider for managing admin view toggle state.
 * This allows admins to switch between admin view (with all admin features)
 * and user view (hiding admin-specific UI elements).
 * State is persisted in localStorage to maintain preference across sessions.
 */
export const AdminViewProvider = ({ children }: AdminViewProviderProps) => {
  const [isAdminViewEnabled, setIsAdminViewEnabled] = useState(() =>
    getStoredAdminViewState()
  )

  // Persist state changes to localStorage
  useEffect(() => {
    setStoredAdminViewState(isAdminViewEnabled)
  }, [isAdminViewEnabled])

  const toggleAdminView = () => {
    setIsAdminViewEnabled((prev) => !prev)
  }

  const setAdminView = (enabled: boolean) => {
    setIsAdminViewEnabled(enabled)
  }

  const value: AdminViewContextType = {
    isAdminViewEnabled,
    toggleAdminView,
    setAdminView,
  }

  return (
    <AdminViewContext.Provider value={value}>
      {children}
    </AdminViewContext.Provider>
  )
}

// Export the context for use in the hook
export { AdminViewContext }
export type { AdminViewContextType }
